import React, { useState, useEffect, useMemo } from 'react';

import OrdersList from '../components/OrdersList';
import PortalSkeleton from '../components/other/PortalSkeleton';
import Error from '../components/other/Error';
import RequestPortalLink, { isExpiredPortalTokenError } from '../components/other/RequestPortalLink';

import { getToken } from '../utils/auth';
import { fetchOrders } from '../api/orders';
import { ORDER_SORT_OPTIONS, ORDER_FILTER_OPTIONS, DEFAULT_DRAFT_ORDER_LIMIT as DEFAULT_ORDER_LIMIT } from '../../utils/constants';
import { CartIcon } from '../../utils/icons';

function OrdersListPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [filterBy, setFilterBy] = useState('all');

  const [nextCursor, setNextCursor] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    const { token } = getToken();

    if (!token) {
      setError('No authentication token found. Please use the link from your email.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await fetchOrders(token, { limit: DEFAULT_ORDER_LIMIT });
      setOrders(data.orders || []);
      setNextCursor(data.pageInfo?.endCursor ?? null);
      setHasNextPage(!!data.pageInfo?.hasNextPage);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  async function loadMore() {
    if (!hasNextPage || loadingMore) return;
    const { token } = getToken();
    if (!token) return;

    try {
      setLoadingMore(true);
      const data = await fetchOrders(token, { cursor: nextCursor, limit: DEFAULT_ORDER_LIMIT });
      setOrders((prev) => [...prev, ...(data.orders || [])]);
      setNextCursor(data.pageInfo?.endCursor ?? null);
      setHasNextPage(!!data.pageInfo?.hasNextPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }

  const filteredAndSortedOrders = useMemo(() => {
    let list = [...(orders || [])];

    if (filterBy !== 'all') {
      list = list.filter((order) => {
        const outstanding = parseFloat(order.totalOutstanding);

        switch (filterBy) {
          case 'unfulfilled':
            return !order.cancelledAt && order.displayFulfillmentStatus === 'UNFULFILLED';
          case 'partially_fulfilled':
            return !order.cancelledAt && order.displayFulfillmentStatus === 'PARTIALLY_FULFILLED';
          case 'fulfilled':
            return !order.cancelledAt && order.displayFulfillmentStatus === 'FULFILLED';
          case 'balance_due':
            return !order.cancelledAt && Number.isFinite(outstanding) && outstanding > 0;
          case 'cancelled':
            return !!order.cancelledAt;
          default:
            return true;
        }
      });
    }

    const key = sortBy;
    const isDateKey = key === 'createdAt' || key === 'updatedAt';
    const isNumericKey = key === 'totalPrice';

    list.sort((a, b) => {
      const va = isDateKey
        ? (a[key] ? new Date(a[key]).getTime() : 0)
        : isNumericKey
          ? (a[key] != null ? parseFloat(a[key]) || 0 : 0)
          : (a[key] || '').toLowerCase();
      const vb = isDateKey
        ? (b[key] ? new Date(b[key]).getTime() : 0)
        : isNumericKey
          ? (b[key] != null ? parseFloat(b[key]) || 0 : 0)
          : (b[key] || '').toLowerCase();

      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;

      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

    return list;
  }, [orders, sortBy, sortDir, filterBy]);

  const handleSortChange = (newSortBy) => {
    if (newSortBy === sortBy) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      const opt = ORDER_SORT_OPTIONS.find((o) => o.value === newSortBy);
      setSortBy(newSortBy);
      setSortDir(opt?.dirDefault || 'asc');
    }
  };

  const isDefaultSort = sortBy === 'createdAt' && sortDir === 'desc';
  const isDefaultFilter = filterBy === 'all';
  const shouldShowSorting = orders.length > 2 || !isDefaultSort || !isDefaultFilter;

  return (
    <main className="">
      {loading && <PortalSkeleton variant="list" />}

      {error && isExpiredPortalTokenError(error) && (
        <RequestPortalLink expiredToken={getToken().token} />
      )}

      {error && !isExpiredPortalTokenError(error) && (
        <Error message={error} onRetry={loadOrders} title="Unable to Load Orders" />
      )}

      {!loading && !error && orders.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">
            <CartIcon />
          </div>
          <h2 className="heading heading--h2">No Orders Yet</h2>
          <p>You don't have any orders at this time.</p>
        </div>
      )}

      {!loading && !error && orders.length > 0 && (
        <OrdersList
          orders={filteredAndSortedOrders}
          sortBy={sortBy}
          sortDir={sortDir}
          sortOptions={ORDER_SORT_OPTIONS}
          onSortChange={handleSortChange}
          filterBy={filterBy}
          filterOptions={ORDER_FILTER_OPTIONS}
          onFilterChange={setFilterBy}
          showSorting={shouldShowSorting}
          hasNextPage={hasNextPage}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
        />
      )}
    </main>
  );
}

export default OrdersListPage;
